import { useState } from "react"
import Image from "next/image"
import { ChevronDown, Flag } from "lucide-react"
import { Poppins } from "next/font/google"

import { useCurrentBranch } from "@/context/current-branch-provider"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/popadvise/reservations/dialog"

const poppins = Poppins({
    subsets: ['latin'],
    weight: ['400', '500', '600', '700'],
})

export function DefaultBranchSelector() {

    const [open, setOpen] = useState(false)
    const { brand, currentBranch } = useCurrentBranch()

    return <>
        <button onClick={() => setOpen(true)} type="button" className="flex text-start items-center gap-4 justify-self-end hover:scale-95 transition-transform">
            <Image
                className="h-10 w-10 rounded-full object-cover"
                src={brand.logo.url || "/images/placeholder.svg"}
                alt="Logo"
                width={40}
                height={40}
            />
            <div className="text-white">
                <h2 className="font-semibold text-sm mb-1">{currentBranch.name}</h2>
                {brand.branches.length > 1 && <p className="font-medium text-xs">Selecciona una sucursal</p>}
            </div>
            {brand.branches.length > 1 && <ChevronDown className="h-5 w-5 text-white" />}
        </button>
        <BranchSelectorDialog open={open} setOpen={setOpen} />
    </>
}

export function BranchSelectorDialog({ open, setOpen }: {
    open: boolean
    setOpen: React.Dispatch<React.SetStateAction<boolean>>
}) {

    const { currentBranch, brand, setCurrentBranch } = useCurrentBranch()

    return (
        <Dialog open={open} onOpenChange={() => setOpen(!open)}>
            <DialogContent className={`sm:rounded-3xl sm:max-w-[670px] ${poppins.className}`} >
                <DialogHeader className="mb-5">
                    <DialogTitle className="text-left">{currentBranch.name}</DialogTitle>
                    <DialogDescription className="text-[#6C6C6C] text-left">
                        Selecciona una sucursal
                    </DialogDescription>
                </DialogHeader>
                <div className="grid gap-2">
                    {
                        brand.branches.map(branch => (
                            <BranchCard
                                key={branch.id}
                                name={branch.name}
                                address={branch.address.address}
                                selected={branch.id === currentBranch.id}
                                onSelect={() => setCurrentBranch(branch)}
                            />
                        ))
                    }
                </div>
            </DialogContent>
        </Dialog>
    )
}

function BranchCard({ name, address, selected, onSelect }) {
    return (
        <div className="border rounded-lg shadow-xs p-6 flex items-center justify-between cursor-pointer" onClick={onSelect}>
            <div className="flex items-center gap-3">
                {
                    !selected
                        ? <span className="w-5 h-5 rounded-full bg-white border shadow-xs" />
                        : (
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <g filter="url(#filter0_d_142_5299)">
                                    <path d="M2 11C2 5.47715 6.47715 1 12 1C17.5228 1 22 5.47715 22 11C22 16.5228 17.5228 21 12 21C6.47715 21 2 16.5228 2 11Z" fill="#161619" />
                                    <path d="M17.3406 8.15469C17.5284 7.87223 17.4984 7.48752 17.2499 7.2376C16.9661 6.95198 16.5044 6.95058 16.2188 7.23447L10.2366 13.1805L7.78115 10.7412C7.49546 10.4573 7.03378 10.4588 6.74996 10.7446C6.6133 10.8821 6.54279 11.0605 6.53831 11.2402C6.538 11.2529 6.53801 11.2656 6.53835 11.2783C6.54326 11.459 6.61497 11.6383 6.75335 11.7757L9.72278 14.7257C10.0072 15.0083 10.4664 15.0082 10.7507 14.7256L17.2469 8.26879C17.2825 8.2333 17.3138 8.19504 17.3406 8.15469Z" fill="white" />
                                </g>
                                <defs>
                                    <filter id="filter0_d_142_5299" x="0" y="0" width="24" height="24" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                                        <feFlood floodOpacity="0" result="BackgroundImageFix" />
                                        <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                                        <feOffset dy="1" />
                                        <feGaussianBlur stdDeviation="1" />
                                        <feColorMatrix type="matrix" values="0 0 0 0 0.894118 0 0 0 0 0.898039 0 0 0 0 0.905882 0 0 0 0.24 0" />
                                        <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_142_5299" />
                                        <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_142_5299" result="shape" />
                                    </filter>
                                </defs>
                            </svg>
                        )
                }
                <div>
                    <h3 className="font-medium text-[#161619]">{name}</h3>
                    <p className="text-xs opacity-80 line-clamp-1 md:hhidden">{address}</p>
                </div>
            </div>
            <div className="hidden md:flex items-center gap-2">
                <Flag className="h-3 w-3" />
                <p className="text-[#6C6C6C] text-sm">{address}</p>
            </div>
        </div>
    )
}